import { evaluate, unaryTest } from 'feelin'
import { DecisionTable, DecisionTableInput, OutputEntries } from './types'

function evaluateFeel(
  expression: string,
  context: { [key: string]: unknown }
): unknown {
  try {
    return evaluate(expression, context).value
  } catch {
    return undefined
  }
}

function evaluateUnaryTest(
  expression: string,
  inputValue: unknown,
  context: { [key: string]: unknown }
): boolean {
  try {
    return unaryTest(expression, { ...context, '?': inputValue }).value === true
  } catch {
    return false
  }
}

function coerceInputValue(
  raw: unknown,
  type: DecisionTableInput['type']
): string | number | boolean | undefined {
  if (raw === undefined || raw === null) return undefined

  const str = String(raw)

  switch (type) {
    case 'number': {
      const n = Number(str)
      return isNaN(n) ? undefined : n
    }
    case 'boolean':
      if (str === 'true') return true
      if (str === 'false') return false
      return undefined
    case 'string':
    default:
      return str
  }
}

export function evaluateDecisionTable(
  table: DecisionTable,
  resolvedData: { [variableName: string]: string | undefined }
): OutputEntries | OutputEntries[] {
  const inputValues: {
    [inputUuid: string]: string | number | boolean | undefined
  } = {}

  for (const input of table.inputs) {
    const raw = evaluateFeel(input.expression, resolvedData)
    inputValues[input.uuid] = coerceInputValue(raw, input.type)
  }

  const matchingOutputEntries: OutputEntries[] = []

  for (const rule of table.rules) {
    const allInputsMatch = table.inputs.every((input) => {
      const entry = rule.input_entries[input.uuid]
      if (!entry || entry === '-') return true

      return evaluateUnaryTest(entry, inputValues[input.uuid], resolvedData)
    })

    if (allInputsMatch) {
      matchingOutputEntries.push(rule.output_entries)
    }
  }

  switch (table.hit_policy) {
    case 'UNIQUE':
      if (matchingOutputEntries.length > 1) {
        console.warn(
          `[DMN] Hit policy UNIQUE violated in table "${table.des}" (${table.uuid}): ${matchingOutputEntries.length} rules matched, expected at most 1.`
        )
      }
      return matchingOutputEntries[0] ?? []

    case 'FIRST':
      return matchingOutputEntries[0] ?? []

    case 'ANY': {
      const serialized = matchingOutputEntries.map((e) => JSON.stringify(e))
      const allSame = serialized.every((s) => s === serialized[0])
      if (!allSame) {
        console.warn(
          `[DMN] Hit policy ANY violated in table "${table.des}" (${table.uuid}): matching rules produced different outputs.`
        )
      }
      return matchingOutputEntries[0] ?? []
    }

    case 'RULE_ORDER':
    case 'COLLECT':
      return matchingOutputEntries
  }
}
